import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { HerramientaEnCalle } from '@/lib/supabase/tipos-bd'
import {
  useDevolverAMano,
  useOrdenesActivas,
  useSolicitarTraslado,
} from '@/modules/programacion/useHerramientas'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import estilos from './HerramientasEnCalle.module.css'

/**
 * Qué hay afuera y con quién.
 *
 * VA ENCIMA DEL CATÁLOGO porque responde la pregunta que se hace de verdad.
 * Nadie abre esta pantalla a preguntarse cuántas escaleras compró la empresa
 * —eso se sabe—; se abre a buscar la que no aparece.
 *
 * Y ordenado por días afuera, de más a menos: la de arriba es siempre la que
 * lleva más tiempo sin volver, que es la que se está perdiendo.
 */

function useEnCalle() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'en-calle'],
    queryFn: async (): Promise<HerramientaEnCalle[]> => {
      const { data, error } = await supabase
        .from('vista_herramientas_en_calle')
        .select('*')
        .order('dias_afuera', { ascending: false })

      if (error) throw new Error(mensajeDeError(error))
      return data ?? []
    },
  })

  return { enCalle: consulta.data ?? [], cargando: consulta.isLoading }
}

export function HerramientasEnCalle() {
  const { enCalle, cargando } = useEnCalle()
  const devolver = useDevolverAMano()
  const solicitarTraslado = useSolicitarTraslado()
  const { ordenes } = useOrdenesActivas()
  const [recibiendo, setRecibiendo] = useState<string | null>(null)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Traslado: a qué orden y por qué. Queda pendiente de aprobación de gerencia.
  const [trasladando, setTrasladando] = useState<string | null>(null)
  const [destino, setDestino] = useState('')
  const [motivoTraslado, setMotivoTraslado] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  // Las que quedaron afuera con la orden ya terminada. Son las que de verdad
  // se pierden: nadie las está usando y nadie las está esperando.
  const olvidadas = enCalle.filter((h) => h.la_orden_ya_termino).length

  async function recibir(prestamoId: string) {
    setError(null)
    try {
      await devolver.mutateAsync({ prestamoId, motivo })
      setRecibiendo(null)
      setMotivo('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo recibir.')
    }
  }

  async function pedirTraslado(prestamoId: string) {
    setError(null)
    setAviso(null)
    try {
      await solicitarTraslado.mutateAsync({
        prestamoId,
        ordenDestinoId: destino,
        motivo: motivoTraslado,
      })
      setTrasladando(null)
      setDestino('')
      setMotivoTraslado('')
      setAviso('Traslado solicitado. Queda pendiente de aprobación de gerencia.')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo solicitar el traslado.')
    }
  }

  if (cargando) return null

  if (enCalle.length === 0) {
    return (
      <Tarjeta subtitulo="Qué hay afuera y con quién">
        <p className={estilos.vacio}>
          <b>Todo está en bodega.</b> Ninguna herramienta salió con una orden abierta.
        </p>
      </Tarjeta>
    )
  }

  return (
    <Tarjeta
      subtitulo="Qué hay afuera y con quién · lo normal es que vuelva escaneada en bodega"
      sinRelleno
      // Viva solo cuando hay algo olvidado afuera: ahí sí espera una decisión
      // de una persona. Con todo en curso es una lista más y no debe gritar.
      tono={olvidadas > 0 ? 'viva' : 'normal'}
    >
      {olvidadas > 0 && (
        <div className={estilos.error}>
          <Aviso tipo="alerta" titulo={`${olvidadas} sin volver, con la orden ya terminada`}>
            El trabajo se cerró y la herramienta sigue afuera. Nadie la está usando y nadie la está
            esperando: son las que se pierden.
          </Aviso>
        </div>
      )}

      {error && (
        <div className={estilos.error}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      {aviso && (
        <div className={estilos.error}>
          <Aviso tipo="exito">{aviso}</Aviso>
        </div>
      )}

      <ul className={estilos.lista}>
        {enCalle.map((h) => (
          <li key={h.prestamo_id} className={h.la_orden_ya_termino ? estilos.filaOlvidada : estilos.fila}>
            <div className={estilos.que}>
              <span className={estilos.nombre}>{h.herramienta}</span>
              <span className={estilos.codigo}>{h.etiqueta}</span>
            </div>

            <div className={estilos.quien}>
              <span className={estilos.persona}>{h.colaborador ?? 'Sin asignar'}</span>
              {/* Sin orden la herramienta está a nombre de la persona: se dice,
                  porque un hueco en blanco parece un dato que no cargó. */}
              <span className={estilos.orden}>{h.orden_numero ?? 'a su nombre'}</span>
            </div>

            <div className={estilos.cuando}>
              <b className={h.dias_afuera >= 7 ? estilos.viejo : undefined}>
                {h.dias_afuera === 0 ? 'Hoy' : `${h.dias_afuera} d`}
              </b>
              {h.la_orden_ya_termino && (
                <span className={estilos.terminada}>
                  {h.orden_numero ? 'Orden terminada' : 'Más de una semana'}
                </span>
              )}
            </div>

            {trasladando === h.prestamo_id ? (
              <div className={estilos.recibiendo}>
                {/* A qué orden se traslada y por qué. No mueve nada solo: queda
                    pendiente y la herramienta no cambia de obra hasta que
                    gerencia lo apruebe. */}
                <select
                  className={estilos.novedad}
                  value={destino}
                  onChange={(e) => setDestino(e.target.value)}
                  autoFocus
                >
                  <option value="">¿A qué orden se traslada?</option>
                  {ordenes
                    .filter((o) => o.id !== h.orden_trabajo_id)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.numero} · {o.cliente ?? 'sin cliente'}
                      </option>
                    ))}
                </select>
                <input
                  type="text"
                  className={estilos.novedad}
                  placeholder="Motivo del traslado"
                  value={motivoTraslado}
                  onChange={(e) => setMotivoTraslado(e.target.value)}
                />
                <Boton
                  tamano="pequeno"
                  onClick={() => void pedirTraslado(h.prestamo_id)}
                  trabajando={solicitarTraslado.isPending}
                  disabled={!destino || motivoTraslado.trim().length === 0}
                >
                  Pedir traslado
                </Boton>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setTrasladando(null)
                    setDestino('')
                    setMotivoTraslado('')
                  }}
                >
                  Cancelar
                </Boton>
              </div>
            ) : recibiendo === h.prestamo_id ? (
              <div className={estilos.recibiendo}>
                {/* El motivo es OBLIGATORIO, y la base lo exige también. Esto
                    es saltarse el escaneo: si se pudiera hacer sin explicar,
                    la vía manual se volvería la vía normal y el QR dejaría de
                    significar nada. */}
                <input
                  type="text"
                  className={estilos.novedad}
                  placeholder="¿Por qué se recibe sin escanear?"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  autoFocus
                />
                <Boton
                  tamano="pequeno"
                  onClick={() => void recibir(h.prestamo_id)}
                  trabajando={devolver.isPending}
                  disabled={motivo.trim().length === 0}
                >
                  Confirmar
                </Boton>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setRecibiendo(null)
                    setMotivo('')
                  }}
                >
                  Cancelar
                </Boton>
              </div>
            ) : (
              <div className={estilos.acciones}>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setTrasladando(h.prestamo_id)
                    setDestino('')
                    setMotivoTraslado('')
                    setError(null)
                    setAviso(null)
                  }}
                >
                  Trasladar
                </Boton>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setRecibiendo(h.prestamo_id)
                    setMotivo('')
                    setError(null)
                  }}
                >
                  Recibir a mano
                </Boton>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Tarjeta>
  )
}
